// @ts-check
import { CheckCircleTwoTone, CloseCircleTwoTone, DeleteOutlined, EditOutlined, UserSwitchOutlined } from "@ant-design/icons";
import { Dropdown, Popover } from "antd";
import { dateFormatterWithTime, formatName, isAdminOrTeamLead, safe_get, updateHistoryState } from '~/other/Helper';
import { IoIosMore as MoreIcon } from "react-icons/io";
import { Link } from "react-router-dom";
import { getAssignmentSubmissionLink, getAssignmentStatusText } from '~/components/Forms/AssignmentDataGrid/AssignmentDataGrid.utils';
import useOrganizationId from '~/hooks/useOrganizationId';
import { useHistory } from "react-router-dom/cjs/react-router-dom.min";
import { useCurrentUser } from '~/hooks/reduxHooks';
import { useAssignmentDataGridContext } from '~/components/Forms/AssignmentDataGrid/AssignmentDataGrid.context';
import moment from "moment";

/**
 * @param {AgGridReactLib.CustomCellRendererProps} props
 */
export function AssignmentActionCell({ data: assignment }) {
    const user = useCurrentUser();
    const { setActiveModal } = useAssignmentDataGridContext();

    const userIsAdminOrTeamLead = isAdminOrTeamLead(user);

    if (!assignment || !userIsAdminOrTeamLead) {
        return null;
    }

    const isOverDue = assignment.due_date_timestamp && moment.unix(assignment.due_date_timestamp).isBefore(moment());

    const canExcuse = !assignment.completed && !assignment.excused;
    const canRequire = assignment.excused;
    const canReassign = !assignment.completed && !assignment.excused && !isOverDue;
    const canDelete = !assignment.completed && !assignment.excused && !isOverDue;

    const handleMenuItemClicked = ({ key }) => {
        setActiveModal({
            key,
            assignment,
        });
    };

    const tableMenu = {
        onClick: handleMenuItemClicked,
        items: [
            assignment.excused
                ? {
                      key: "require_assignment",
                      icon: <EditOutlined />,
                      label: "Require",
                      disabled: !canRequire,
                  }
                : {
                      key: "excuse_assignment",
                      icon: <EditOutlined />,
                      label: "Excuse",
                      disabled: !canExcuse,
                  },
            {
                key: "reassign_assignment",
                icon: <UserSwitchOutlined />,
                label: "Reassign",
                disabled: !canReassign,
            },
            {
                key: "delete_assignment",
                icon: <DeleteOutlined />,
                label: "Delete",
                className: "zero-delete-red",
                disabled: !canDelete,
            },
        ].filter((item) => !!item),
    };

    if (tableMenu.items.length === 0) {
        return null;
    }

    return (
        <div className="more pull-right" style={{ display: "inline-block", color: "grey", lineHeight: 0 }}>
            <Dropdown menu={tableMenu} trigger={["click"]} placement="bottomLeft">
                <button className="ButtonLink ant-dropdown-link">
                    <MoreIcon style={{ height: "20px", width: "20px" }} />
                </button>
            </Dropdown>
        </div>
    );
}

/**
 * @param {AgGridReactLib.CustomCellRendererProps} props
 */
export function AssignmentNameCell({ data: assignment, value: assignedToName, api }) {
    const history = useHistory();
    const orgId = useOrganizationId();

    if (!assignment) {
        return assignedToName;
    }

    const fullName = formatName(assignment.assigned_to, { defaultName: "" });

    // If assignment is completed, link to submission
    if (assignment.completed && assignment.submission_uuid) {
        const linkUrl = getAssignmentSubmissionLink(assignment, orgId);

        if (linkUrl) {
            const onClick = () => {
                updateHistoryState(history, { gridState: api.getState() });
            };

            return (
                <Link to={linkUrl} className="blue-link" onClick={onClick}>
                    {fullName}
                </Link>
            );
        }
    }

    return <span>{fullName}</span>;
}

/**
 * @param {AgGridReactLib.CustomCellRendererProps} props
 */
export function AssignmentStatusCell(props) {
    const { data: assignment } = props;
    const user = useCurrentUser();

    if (!assignment) {
        return null;
    }

    if (assignment.state === "unassigned") {
        return null;
    } else if (assignment.loading) {
        return <>{renderStatusIcon(assignment)}</>;
    }

    return (
        <Popover content={<AssignmentInfoPopover assignment={assignment} user={user} />}>
            {renderStatusIcon(assignment)}
        </Popover>
    );
}

/**
 * Render status icon based on assignment state
 * @param {any} assignment
 * @returns {JSX.Element}
 */
function renderStatusIcon(assignment) {
    let grey = "#B5B5B5";
    let red = "#f56946";
    let green = "#52c41a";
    let yellow = "#f3b44b";

    let isOverDue = assignment.due_date_timestamp && moment.unix(assignment.due_date_timestamp).isBefore(moment());

    if (assignment.shared_submission_uuid) {
        return (
            <span>
                <CheckCircleTwoTone twoToneColor={grey} /> Completed
            </span>
        );
    } else if (assignment.completed) {
        return (
            <span>
                <CheckCircleTwoTone twoToneColor={green} /> Completed
            </span>
        );
    } else if (assignment.excused) {
        return (
            <span>
                <CloseCircleTwoTone twoToneColor={yellow} /> Excused
            </span>
        );
    } else if (isOverDue) {
        return (
            <span>
                <CloseCircleTwoTone twoToneColor={red} /> Missed
            </span>
        );
    } else {
        return (
            <span>
                <CloseCircleTwoTone twoToneColor={grey} /> Open
            </span>
        );
    }
}

/**
 * Assignment info popover content
 * @param {{assignment: any, user: any}} props
 */
function AssignmentInfoPopover({ assignment, user }) {
    const orgId = useOrganizationId();

    const hrStyle = {
        marginTop: 10,
        marginBottom: 10,
    };

    const bold = {
        fontWeight: 600,
    };

    let due_date_timestamp = safe_get(assignment, "due_date_timestamp", null);
    if (!due_date_timestamp) {
        let dt = new Date();
        due_date_timestamp = dt.setUTCHours(23, 59, 0, 0) / 1000;
    }

    let assignment_due_date = `${moment.unix(due_date_timestamp).format("MMM D, YYYY")} at ${moment
        .unix(due_date_timestamp)
        .format("hh:mm A")}`;

    let isOverDue = moment.unix(due_date_timestamp).isBefore(moment());

    let team_uuid = "my_teams";

    if (assignment.teams) {
        team_uuid = assignment.teams[0].uuid;
    }

    let form_type = assignment.form_type === 3 ? "courses" : "forms";

    return (
        <div style={{ padding: 8 }}>
            <span className="zero-blue" style={bold}>
                Assigned on:{" "}
            </span>
            {dateFormatterWithTime(assignment.assigned_at)}
            <hr style={hrStyle} />
            <span className="zero-blue" style={bold}>
                Due on:{" "}
            </span>
            {assignment_due_date}
            {!assignment.excused && (
                <>
                    <hr style={hrStyle} />
                    <span className="zero-blue" style={bold}>
                        Completed on:{" "}
                    </span>
                    {assignment.completed ? dateFormatterWithTime(assignment.completed_at) : "N/A"}
                    <hr style={hrStyle} />
                    <span className="zero-blue" style={bold}>
                        Submitted by:{" "}
                    </span>
                    {assignment.completed ? assignment.assigned_to.full_name : "N/A"}
                    {assignment.completed && assignment.submission_uuid && (
                        <>
                            <hr style={hrStyle} />
                            <Link
                                to={`/${orgId}/home/team/${team_uuid}/${form_type}/submission_view/${assignment.submission_uuid}`}
                                className="btn-link"
                            >
                                View Submission
                            </Link>
                        </>
                    )}
                </>
            )}
            {assignment.excused && (
                <>
                    <hr style={hrStyle} />
                    <span className="zero-blue" style={bold}>
                        Excused on:{" "}
                    </span>
                    {assignment.excused_at ? dateFormatterWithTime(assignment.excused_at) : "N/A"}
                    <hr style={hrStyle} />
                    <span className="zero-blue" style={bold}>
                        Excused by:{" "}
                    </span>
                    {assignment.excused_by ? assignment.excused_by.full_name : "N/A"}
                </>
            )}
        </div>
    );
}
